import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, LogOut, Package, Upload, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import client, { formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Cadenas", "Pulseras", "Dijes", "Pendientes"];
const EMPTY = { name: "", description: "", price: "", category: "Cadenas", image_url: "", in_stock: true, featured: false };

function ProductForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial, price: initial?.price ?? "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir la imagen");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, price: parseFloat(form.price) || 0 });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="product-form">
      <div className="space-y-2">
        <Label>Nombre</Label>
        <Input data-testid="product-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required className="bg-secondary border-border" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Precio (USD)</Label>
          <Input data-testid="product-price-input" type="number" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} required className="bg-secondary border-border" />
        </div>
        <div className="space-y-2">
          <Label>Categoría</Label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="product-category-input" className="bg-secondary border-border"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`product-category-option-${c.toLowerCase()}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Imagen del producto</Label>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleFile} className="hidden" data-testid="product-image-file" />
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          data-testid="product-image-dropzone"
          className="border border-dashed border-white/20 rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:border-white/40 transition-colors bg-secondary/40"
        >
          {form.image_url ? (
            <img src={form.image_url} alt="preview" className="h-16 w-16 rounded object-cover border border-white/10" />
          ) : (
            <div className="h-16 w-16 rounded bg-secondary flex items-center justify-center"><Upload className="h-5 w-5 text-muted-foreground" /></div>
          )}
          <div className="text-sm">
            {uploading ? (
              <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Subiendo…</span>
            ) : (
              <>
                <p className="font-medium">{form.image_url ? "Cambiar imagen" : "Subir desde tu PC"}</p>
                <p className="text-xs text-muted-foreground">JPG, PNG, WEBP o GIF · máx 8MB</p>
              </>
            )}
          </div>
        </div>
        <Input data-testid="product-image-input" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="o pega una URL de imagen…" className="bg-secondary border-border text-xs" />
      </div>

      <div className="space-y-2">
        <Label>Descripción</Label>
        <Textarea data-testid="product-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} className="bg-secondary border-border" />
      </div>
      <div className="flex items-center gap-8">
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="product-instock-switch" checked={form.in_stock} onCheckedChange={(v) => set("in_stock", v)} /> En stock
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Switch data-testid="product-featured-switch" checked={form.featured} onCheckedChange={(v) => set("featured", v)} /> Destacado
        </label>
      </div>
      <DialogFooter>
        <Button type="submit" data-testid="save-product-button" disabled={saving || uploading} className="bg-primary text-primary-foreground hover:bg-white/90 uppercase tracking-wide">
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function ImageUploader({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);
  const ref = useRef(null);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await client.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo subir la imagen");
    } finally { setUploading(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handle} className="hidden" data-testid={`${testid}-file`} />
      <div onClick={() => !uploading && ref.current?.click()} data-testid={`${testid}-dropzone`}
        className="border border-dashed border-white/20 rounded-lg overflow-hidden cursor-pointer hover:border-white/40 transition-colors bg-secondary/40">
        {value ? (
          <div className="relative">
            <img src={value} alt={label} className="w-full h-32 object-cover" />
            <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center text-sm">
              {uploading ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Subiendo…</span> : "Cambiar imagen"}
            </div>
          </div>
        ) : (
          <div className="h-32 flex flex-col items-center justify-center gap-2 text-muted-foreground text-sm">
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
            Subir desde tu PC
          </div>
        )}
      </div>
    </div>
  );
}

function SiteImagesDialog() {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState({ hero_image: "", experience_image: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) client.get("/settings").then((r) => setSettings({ hero_image: r.data.hero_image || "", experience_image: r.data.experience_image || "" }));
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await client.put("/settings", settings);
      toast.success("Imágenes del sitio actualizadas");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="site-images-button" className="border-border bg-transparent uppercase tracking-wide gap-2">
          <ImageIcon className="h-4 w-4" /> Imágenes del sitio
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Imágenes del sitio</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <ImageUploader label="Imagen principal (portada)" value={settings.hero_image} testid="hero-image"
            onChange={(v) => setSettings((s) => ({ ...s, hero_image: v }))} />
          <ImageUploader label="Imagen de la sección Experiencia" value={settings.experience_image} testid="experience-image"
            onChange={(v) => setSettings((s) => ({ ...s, experience_image: v }))} />
        </div>
        <DialogFooter>
          <Button onClick={save} data-testid="save-site-images-button" disabled={saving} className="bg-primary text-primary-foreground hover:bg-white/90 uppercase tracking-wide">
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => client.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const create = async (data) => { await client.post("/products", data); toast.success("Producto creado"); load(); };
  const update = async (id, data) => { await client.put(`/products/${id}`, data); toast.success("Producto actualizado"); load(); };
  const remove = async (id) => { await client.delete(`/products/${id}`); toast.success("Producto eliminado"); load(); };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-white/10 px-6 md:px-12 h-20 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5" />
          <div>
            <p className="font-display text-lg leading-none">Panel de administración</p>
            <p className="text-xs text-muted-foreground mt-1">{user?.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a href="/" className="text-sm text-muted-foreground hover:text-foreground">Ver tienda</a>
          <Button variant="outline" size="sm" data-testid="logout-button" onClick={doLogout} className="border-border bg-transparent gap-2">
            <LogOut className="h-4 w-4" /> Salir
          </Button>
        </div>
      </header>

      <main className="px-6 md:px-12 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl">Productos</h1>
            <p className="text-sm text-muted-foreground mt-1">{products.length} en catálogo</p>
          </div>
          <div className="flex items-center gap-3">
          <SiteImagesDialog />
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-product-button" className="bg-primary text-primary-foreground hover:bg-white/90 uppercase tracking-wide gap-2">
                <Plus className="h-4 w-4" /> Nuevo producto
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo producto</DialogTitle></DialogHeader>
              <ProductForm onSave={create} onClose={() => setAddOpen(false)} />
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="border border-white/10 rounded-lg overflow-hidden bg-card">
          <Table data-testid="products-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="w-16"></TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead className="text-right">Precio</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((p) => (
                <TableRow key={p.id} data-testid={`admin-row-${p.id}`} className="border-border">
                  <TableCell><img src={p.image_url} alt={p.name} className="h-10 w-10 rounded object-cover border border-white/10" /></TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-muted-foreground">{p.category}</TableCell>
                  <TableCell>{p.in_stock ? <span className="text-success">Disponible</span> : <span className="text-muted-foreground">Agotado</span>}</TableCell>
                  <TableCell className="text-right font-mono">${p.price.toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Dialog open={editing?.id === p.id} onOpenChange={(o) => setEditing(o ? p : null)}>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`edit-${p.id}`} className="h-8 w-8"><Pencil className="h-4 w-4" /></Button>
                        </DialogTrigger>
                        <DialogContent className="bg-card border-border max-w-lg">
                          <DialogHeader><DialogTitle className="font-display text-2xl">Editar producto</DialogTitle></DialogHeader>
                          <ProductForm initial={p} onSave={(d) => update(p.id, d)} onClose={() => setEditing(null)} />
                        </DialogContent>
                      </Dialog>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`delete-${p.id}`} className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-card border-border">
                          <AlertDialogHeader>
                            <AlertDialogTitle>¿Eliminar "{p.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-secondary border-border">Cancelar</AlertDialogCancel>
                            <AlertDialogAction data-testid={`confirm-delete-${p.id}`} onClick={() => remove(p.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Eliminar</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
